import type { ListBuilderConfiguration } from '../types';

/**
 * Purchase Order Detail (Line Items) list configuration
 * Shows line items within a purchase order with quantities, costs, and expected dates
 *
 * Laid out like the order and invoice line-item lists (PD-15581 follow-up):
 * description over item number in one column with the description linked to the
 * item, then the quantities and the costs stacked and right-aligned. It was
 * eight flat columns before, with the item number and description both painted
 * link-blue while neither was clickable and the quantities carrying decimals
 * they never need.
 */
export const PURCHASE_ORDER_DETAIL_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-purchase-order-details-all',
			cacheTTL: 600000,
			name: 'All Purchase Order Line Items',
			type: 'default',
			active: true,
			columns: [
				{
					// Description over item number, the description linked to the item —
					// the same shape the order and invoice line-item lists use. The two
					// used to be separate columns, both coloured like links while neither
					// was one. Column id kept as `item-description` because
					// `defaultSortId` below is derived from it.
					id: 'item-description',
					fields: [
						{
							id: 'item-description-field',
							// `itemId` is attached per row by
							// PurchaseOrdersService.getPurchaseOrderDetails.
							link: {
								enabled: true,
								type: 'object',
								objectTable: 'item',
								idField: 'itemId',
							},
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item_description',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'theme',
									themeColor: 'link',
								},
								fontWeight: 'semibold',
							},
							displayStyle: 'text',
						},
						{
							id: 'item-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'line_break',
						},
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'item_external_id',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-description', text: 'Description' },
						{ id: 'h-item-number', text: 'Item #' },
					],
					minWidth: 250,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'item_description',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'expected-date',
					fields: [
						{
							id: 'expected-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'expectedReceiptDate',
								},
								defaultValue: '-',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-expected-date', text: 'Expected' }],
					minWidth: 110,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'expectedReceiptDate',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'top',
				},
				{
					// Ordered / Received / Backordered stacked in one right-aligned
					// column. `numberFormat: 'auto'` drops the trailing decimals — these
					// are whole units, and three columns of "12.00" wasted the width the
					// description needs.
					id: 'quantities',
					fields: [
						{
							id: 'qty-ordered-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityOrdered',
								},
								defaultValue: '-',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
								fontWeight: 'semibold',
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
						{
							id: 'qty-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'qty-received-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityReceived',
								},
								defaultValue: '-',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'success',
								},
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
						{
							id: 'qty-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'qty-backordered-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantityBackordered',
								},
								defaultValue: '-',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'warning',
								},
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
					],
					headers: [
						{ id: 'h-qty-ordered', text: 'Ordered' },
						{ id: 'h-qty-received', text: 'Received' },
						{ id: 'h-backordered', text: 'Backordered' },
					],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantityOrdered',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'top',
				},
				{
					// Unit cost over line total, stacked and right-aligned for the same
					// reason as the quantities.
					id: 'unit-cost',
					fields: [
						{
							id: 'unit-cost-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'cost',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'currency',
						},
						{
							id: 'cost-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'line-total-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lineTotal',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h-unit-cost', text: 'Unit Cost' },
						{ id: 'h-total', text: 'Total' },
					],
					minWidth: 100,
					sortable: true,
					alignment: 'right',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'cost',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'small',
					},
					verticalAlignment: 'top',
				},
				{
					// PD-14586: per-line short-close. The callback (wired by
					// PurchaseOrderLineItemsCard) guards already-cancelled /
					// nothing-remaining lines, since row actions have no
					// per-row visibility predicate.
					id: 'actions',
					headers: [{ id: 'h-actions', text: 'Actions' }],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'cancel-remaining-action',
									label: 'Cancel Remaining',
									iconName: 'ban',
									callbackName: 'cancelRemainingLine',
								},
							],
						},
					],
					sortable: false,
					width: 90,
					alignment: 'center',
					verticalAlignment: 'middle',
				},
			],
			description: 'List of purchase order line items',
			defaultSortId: 'column-item-description-asc',
			requiredFields: [
				'purchaseOrderDetail._id',
				'purchaseOrderDetail.externalId',
				'purchaseOrderDetail.quantityOrdered',
				'purchaseOrderDetail.quantityReceived',
				'purchaseOrderDetail.quantityBackordered',
				'purchaseOrderDetail.cost',
				'purchaseOrderDetail.expectedReceiptDate',
				'purchaseOrderDetail.cancelledDate',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			// Object link resolver (useListLinkNavigation) finds the entry whose
			// objectTable matches the field's `link.objectTable`; without an
			// 'item' entry here, the description's item link falls back to the
			// purchase_order_detail baseUrl below. Mirrors order_detail.config.ts.
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [],
		},
		{
			baseUrl: '/purchase-orders/:purchaseOrderId/details/:id',
			objectLabel: 'Purchase Order Detail',
			objectTable: 'purchase_order_detail',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'item_external_id', fieldLabel: 'Item #' },
				{ fieldName: 'item_description', fieldLabel: 'Description' },
				{ fieldName: 'itemId', fieldLabel: 'Item ID' },
				{ fieldName: 'expectedReceiptDate', fieldLabel: 'Expected Date' },
				{ fieldName: 'quantityOrdered', fieldLabel: 'Qty Ordered' },
				{ fieldName: 'quantityReceived', fieldLabel: 'Qty Received' },
				{
					fieldName: 'quantityBackordered',
					fieldLabel: 'Qty Backordered',
				},
				{ fieldName: 'cost', fieldLabel: 'Unit Cost' },
			],
		},
	],
	tableSorts: [
		{
			id: 'item-description',
			name: 'Description',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'item_description',
				},
			],
			description: 'Sort by item description',
		},
		{
			id: 'expected-date',
			name: 'Expected Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'expectedReceiptDate',
				},
			],
			description: 'Sort by expected date (newest first)',
		},
		{
			id: 'qty-ordered',
			name: 'Qty Ordered',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'quantityOrdered',
				},
			],
			description: 'Sort by quantity ordered (highest first)',
		},
		{
			id: 'unit-cost',
			name: 'Unit Cost',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'cost',
				},
			],
			description: 'Sort by unit cost (highest first)',
		},
	],
};
